import { Box, Divider, Grid2 as Grid } from "@mui/material";
import { DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useFrameworkTemplateControlDialog } from "./FrameworkTemplateControlDialog.hooks.ts";
import { FrameworkTemplateControlSaveModel } from "./FrameworkTemplateControlDialog.types.ts";
import SelectableAnnualWheelActivityTable from "../../../../common/components/selectableAnnualWheelActivityTable/SelectableAnnualWheelActivityTable.tsx";
import DotLegalTabs from "../../../../common/components/dotLegalTabs/DotLegalTabs.tsx";
import { useState } from "react";
import { TabContent } from "../../../../common/components/dotLegalTabs/DotLegalTabs.types.ts";

export interface FrameworkTemplateControlDialogProps {
    frameworkTemplateId: string;
    controlId?: string;
    onCloseDialog: () => void;
    onControlSaved: () => void;
    isLocked?: boolean;
    isLoading?: boolean;
    control?: FrameworkTemplateControlSaveModel;
}

function FrameworkTemplateControlDialog(props: FrameworkTemplateControlDialogProps) {
    const [selectedLangTab, setSelectedLangTab] = useState(0);

    const {
        isEdit,
        saveModel,
        modelUpdater,
        showError,
        isSaving,
        categoriesIsLoading,
        selectableCategories,
        annualWheelActivitiesIsLoading,
        selectableAnnualWheelActivities,
        onSaveClick,
    } = useFrameworkTemplateControlDialog(props);

    const tabContent: Array<TabContent> = ["en", "da"].map((langCode, i) => {
        const lang = saveModel.languages.find((l) => l.twoLetterIsoLanguage === langCode)!;
        return {
            index: i,
            label: langCode,
            content: (
                <>
                    <Box marginBottom={2}>
                        <DotLegalTextField
                            disabled={props.isLocked}
                            label={"Name of control"}
                            value={lang.name}
                            onChange={(name) => modelUpdater.onNameChange(langCode, name)}
                            errorText={showError && saveModel.isNameInvalid() ? "Name is required" : ""}
                            debounce={false}
                            noMargin
                        />
                    </Box>

                    <DotLegalTextField
                        disabled={props.isLocked}
                        label={"Description"}
                        value={lang.description ?? ""}
                        debounce={false}
                        multiline
                        rows={3}
                        onChange={(desc) => modelUpdater.onDescriptionChange(langCode, desc)}
                        noMargin
                    />
                </>
            ),
        };
    });

    return (
        <DotLegalDialog
            header={isEdit ? "Edit control" : "Add control to framework"}
            buttonOkText={"Save"}
            open
            onDialogClose={props.onCloseDialog}
            onOkClick={onSaveClick}
            size="lg"
            inProgress={isSaving}
            isLoading={props.isLoading}
            hideOkButton={props.isLocked}
        >
            <Box sx={{ marginLeft: 3, marginRight: 3 }}>
                <Grid container columnSpacing={8}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DotLegalTabs
                            border
                            tabStyle={"secondary"}
                            selectedTab={selectedLangTab}
                            setSelectedTab={setSelectedLangTab}
                            content={tabContent}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <DotLegalTextField
                                disabled={props.isLocked}
                                label="ID"
                                value={saveModel.customId ?? ""}
                                onChange={modelUpdater.onCustomIdChange}
                                debounce={false}
                                noMargin
                            />

                            <DotLegalSelect
                                disabled={props.isLocked}
                                selectedItem={saveModel.categoryId}
                                label={"Category"}
                                noOptionsLabel={"No options"}
                                options={selectableCategories}
                                isLoading={categoriesIsLoading}
                                onChange={modelUpdater.onCategoryChange}
                                errorText={showError && saveModel.isCategoryInvalid() ? "Category is required" : ""}
                                noMargin
                            />

                            <DotLegalTextField
                                label="Implementation group"
                                value={saveModel.implementationGroup ?? ""}
                                onChange={modelUpdater.onImplementationGroupChange}
                                debounce={false}
                                noMargin
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={(theme) => ({ marginTop: 4, marginBottom: 4, borderColor: theme.palette.primary.light })} />

                <Box sx={{ display: "flex", justifyContent: "center", textTransform: "uppercase" }}>
                    <DotLegalHeader center headerStyle={"small"}>
                        Select annual wheel activities in the control
                    </DotLegalHeader>
                </Box>

                <SelectableAnnualWheelActivityTable
                    isLoading={annualWheelActivitiesIsLoading}
                    selectableAnnualWheelActivities={selectableAnnualWheelActivities ?? []}
                    selectedAnnualWheelActivities={saveModel.annualWheelActivities}
                    onAddAnnualWheelActivity={modelUpdater.onAnnualWheelActivityChange}
                    onRemoveAnnualWheelActivity={modelUpdater.onAnnualWheelActivityChange}
                />
            </Box>
        </DotLegalDialog>
    );
}

export default FrameworkTemplateControlDialog;
